import { pickFreshestIso } from "./deviceStatus";

/**
 * Module-level (not React state) "freshest last-contact time seen this session, per asset unit
 * id". Plain component state resets to nothing on every mount, so navigating dashboard → assets →
 * dashboard re-seeds the device list purely from whatever that specific `getAllAssets` response
 * happens to carry — if that response is ever older than what the socket already pushed live
 * moments earlier (see `deviceStatus.pickFreshestIso` for why that can happen), the "last ping"
 * column visibly jumps backwards and the row can flip to Offline even though the device never
 * actually went quiet. This is the same "outlives the component" technique
 * `utils/mobiTrackRealtime.js` already uses for Track Point presence — a plain JS Map living
 * outside React, cleared only on a real page reload / new login.
 */
const freshestByAssetUnitId = new Map();

/**
 * Feed a newly observed last-contact timestamp for an asset (from a REST fetch or a live socket
 * frame) and get back the freshest one seen so far this session — never older than what was
 * already recorded. Call this everywhere `dateTimeF1`/`lastFrameUtc` would otherwise be seeded or
 * updated for a GT06/FT920 tracker.
 */
export function reconcileLastContact(assetUnitId, isoTimestamp) {
  if (!assetUnitId) return isoTimestamp;
  const prev = freshestByAssetUnitId.get(assetUnitId) ?? null;
  const freshest = pickFreshestIso(prev, isoTimestamp);
  if (freshest) freshestByAssetUnitId.set(assetUnitId, freshest);
  return freshest;
}

/** Test-only: reset the cache between test cases / a real logout. */
export function clearLastContactCache() {
  freshestByAssetUnitId.clear();
}
